import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useEffect,
} from 'react';

import { Icon } from '@antoree/ant-ui';

interface Toast {
  id: number;
  message: string;
  type?: 'success' | 'error' | 'info';
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (options: {
    message: string;
    type?: 'success' | 'error' | 'info';
  }) => void;
  removeToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};

let toastId = 0;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  const showToast = (options: {
    message: string;
    type?: 'success' | 'error' | 'info';
  }) => {
    const id = ++toastId;
    const { message, type = 'success' } = options;

    setToasts(prev => [...prev, { id, message, type }]);

    const timeout = setTimeout(() => {
      removeToast(id);
      timeoutsRef.current.delete(id);
    }, 3000);

    timeoutsRef.current.set(id, timeout);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  };

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {toasts.map(toast => {
          const isError = toast.type === 'error';
          const isInfo = toast.type === 'info';
          const borderColor = isError
            ? '#ef4444'
            : isInfo
            ? '#3b82f6'
            : '#14B24C';
          const textColor = borderColor;
          const iconType = isError ? 'cross' : isInfo ? 'alert' : 'check';

          return (
            <div
              key={toast.id}
              style={{
                background: '#fff',
                color: textColor,
                border: `2px solid ${borderColor}`,
                padding: '16px 20px 16px 20px',
                borderRadius: 12,
                marginBottom: 0,
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                minWidth: 260,
                maxWidth: 400,
                fontSize: 16,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                animation: 'toast-fade-in 0.4s',
                position: 'relative',
              }}
            >
              <div
                style={{
                  marginRight: 12,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Icon type={iconType} size="l" />
              </div>
              <span
                style={{
                  flex: 1,
                  whiteSpace: 'pre-line',
                  padding: '6px 0',
                  display: 'inline-block',
                  lineHeight: 1.5,
                  color: '#1f2937',
                }}
              >
                {toast.message}
              </span>
              <button
                onClick={() => removeToast(toast.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: 18,
                  marginLeft: 10,
                  cursor: 'pointer',
                  opacity: 0.7,
                  transition: 'opacity 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label="Đóng thông báo"
              >
                <Icon type="cross" size="m" />
              </button>
              <style>{`
                @keyframes toast-fade-in {
                  from { opacity: 0; transform: translateY(-20px); }
                  to { opacity: 1; transform: translateY(0); }
                }
              `}</style>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
